/**
 * ProvenanceIcons — per-element provider icons for the multi-provider contacts
 * provenance display (§3.5). Given the `sources` set of an email/phone value, it
 * renders one small provider logo per source ACCOUNT with a tooltip naming the
 * account + the signals it vouched for ("Google (a@b.com): address book, email").
 *
 * Self-contained: it fetches the account map itself (shared, cached query) so
 * callers — including the large ContactEditModal — only render the element rows
 * and drop this in, no hook wiring at the call site. Renders nothing when there
 * are no sources (native/legacy contacts) so it's safe to sprinkle everywhere.
 */
import microsoftLogo from '@/common/microsoft-logo.svg';
import { groupSourcesByAccount, type ElementSource } from '../contactProvenance';
import { useProviderAccounts } from '../hooks/useProviderAccounts';

const PROVIDER_LABEL: Record<string, string> = { google: 'Google', microsoft: 'Microsoft' };
const SIGNAL_LABEL: Record<string, string> = {
  contacts: 'address book',
  email: 'email',
  calendar: 'calendar',
  native: 'Xavior',
};

function providerLogo(provider: string): string | null {
  if (provider === 'google') return '/google-logo.svg';
  if (provider === 'microsoft') return microsoftLogo;
  return null;
}

export function ProvenanceIcons({ sources, size = 12 }: { sources: ElementSource[]; size?: number }) {
  // Wrapper: no sources → render nothing AND call no hook, so native/legacy
  // contacts (and tests that render the contact UI without a QueryClientProvider)
  // never touch the account-map query. Only contacts that actually carry
  // provenance mount the inner component that fetches the map.
  if (!sources || sources.length === 0) return null;
  return <ProvenanceIconsInner sources={sources} size={size} />;
}

function ProvenanceIconsInner({ sources, size }: { sources: ElementSource[]; size: number }) {
  const accounts = useProviderAccounts();
  const byAccount = [...groupSourcesByAccount(sources).entries()];
  if (byAccount.length === 0) return null;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
      {byAccount.map(([integrationId, signals]) => {
        const acct = accounts.get(integrationId);
        const provider = acct?.provider ?? '';
        const logo = providerLogo(provider);
        const providerName = PROVIDER_LABEL[provider] ?? (provider ? provider : 'Account');
        const signalText = signals.map((s) => SIGNAL_LABEL[s] ?? s).join(', ');
        const tooltip = acct
          ? `${providerName} (${acct.email})${signalText ? `: ${signalText}` : ''}`
          : `Disconnected account${signalText ? `: ${signalText}` : ''}`;
        return logo ? (
          <img
            key={integrationId}
            src={logo}
            width={size}
            height={size}
            alt={providerName}
            title={tooltip}
            style={{ borderRadius: 2, opacity: 0.9, flexShrink: 0 }}
          />
        ) : (
          <span
            key={integrationId}
            title={tooltip}
            aria-label={providerName}
            style={{ width: size, height: size, borderRadius: '50%', background: '#9ca3af', display: 'inline-block', flexShrink: 0 }}
          />
        );
      })}
    </span>
  );
}
